import React, {
  useEffect, useState, useMemo, useCallback, useRef,
} from 'react';
import {
  geoMercator, geoNaturalEarth1, geoPath, geoCentroid, geoBounds,
} from 'd3-geo';
import * as dispatchApi from '../services/dispatchAnalysisApi';
import {
  getCityCoordinates, cityLooseKey, cleanCityName, editDistance, cityNameTokens,
} from '../data/cityCoordinates';
import SalesGlobe, { webglSupported } from './SalesGlobe';

const WIDTH = 900;
const HEIGHT = 620;

// Beyond this many points on one map, labels drawn next to every dot start
// overlapping into an unreadable mess — past this count, dots render
// without labels (name still available on hover, and always in the table
// beside the map) rather than everyone's name stacked on top of each other.
const MAX_LABELED_POINTS = 12;

const BORDER = '1px solid var(--color-border)';

/**
 * The India states outline file was published before several state renames,
 * so it still uses the old official names for a few states — this maps a
 * real-world spelling (as typed in an Excel) to the outline's name, and also
 * strips "&" vs "and" and extra whitespace.
 */
const STATE_NAME_ALIASES = {
  odisha: 'orissa',
  uttarakhand: 'uttaranchal',
  pondicherry: 'puducherry',
};

function normalizeStateName(name) {
  if (!name) return '';
  const cleaned = name.trim().toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ');
  return STATE_NAME_ALIASES[cleaned] || cleaned;
}

/** File name (without .json) of a state's detailed place list under
 * /data/cities/. */
function stateFileSlug(stateName) {
  return normalizeStateName(stateName).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function normalizeCountryName(name) {
  return (name || '').trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
}

/** Light-to-dark blue, scaled to the highest % actually present, so the
 * darkest region on screen is always the top performer. */
function colorForPct(pct, maxPct) {
  if (maxPct <= 0 || pct <= 0) return '#eef2f7';
  const t = Math.min(1, pct / maxPct);
  const lerp = (a, b) => Math.round(a + (b - a) * t);
  return `rgb(${lerp(219, 23)},${lerp(234, 78)},${lerp(254, 166)})`;
}

/**
 * Fits a projection to a feature so it fills the drawing area. d3-geo's own
 * .fitSize() sets a clipExtent that degenerates into a full-rectangle
 * artifact for some rings, so the same scale/translate is computed by hand
 * and applied without ever setting a clipExtent.
 */
function fitProjection(feature, baseProjection = geoMercator()) {
  const proj = baseProjection;
  const [[x0, y0], [x1, y1]] = geoPath(proj).bounds(feature);
  proj.scale((proj.scale() * 0.95) / Math.max((x1 - x0) / WIDTH, (y1 - y0) / HEIGHT));
  const [[nx0, ny0], [nx1, ny1]] = geoPath(proj).bounds(feature);
  const [tx, ty] = proj.translate();
  proj.translate([(WIDTH - (nx0 + nx1)) / 2 + tx, (HEIGHT - (ny0 + ny1)) / 2 + ty]);
  return proj;
}

/** Collapses size-wise rows (one per group+size) to one entry per group,
 * summing qty and % — a group's own % is the sum of its sizes' %. */
function distinctBy(rows, keyOf, seed) {
  const m = new Map();
  rows.forEach((r) => {
    const k = keyOf(r);
    if (!m.has(k)) m.set(k, { ...seed(r), qty: 0, pct: 0 });
    const e = m.get(k);
    e.qty += r.qty;
    e.pct = Math.round((e.pct + r.pct) * 10) / 10;
  });
  return Array.from(m.values());
}

function Legend({ maxPct, label }) {
  if (maxPct <= 0) return null;
  const stops = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}:</span>
      {stops.map((t) => (
        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 14, height: 14, background: colorForPct(t * maxPct || 0.001, maxPct), border: BORDER, borderRadius: 3 }} />
          <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{Math.round(t * maxPct * 10) / 10}%</span>
        </div>
      ))}
    </div>
  );
}

const tableStyle = {
  width: '100%', borderCollapse: 'collapse', border: BORDER, borderRadius: 'var(--radius-sm)', overflow: 'hidden',
};
const thStyle = {
  padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: '#1e293b', fontWeight: 700, borderRight: BORDER,
};
const tdStyle = { padding: '6px 4px', fontSize: 'var(--font-size-sm)' };

/**
 * The size-wise table used at every level (countries, states, cities,
 * customers): one merged No./Name/Total Box cell per group, one line per
 * size with its % and box count, and a Grand Total row at the bottom.
 * Rows must already be ordered so that a group's rows sit together.
 */
function SizeWiseTable({
  rows, groupOf, nameOf, nameHeader, totalOf, onRowClick, emptyText,
}) {
  const counts = new Map();
  rows.forEach((r) => counts.set(groupOf(r), (counts.get(groupOf(r)) || 0) + 1));
  const seenForTotal = new Set();
  let grandTotal = 0;
  rows.forEach((r) => {
    const g = groupOf(r);
    if (!seenForTotal.has(g)) { seenForTotal.add(g); grandTotal += totalOf(r); }
  });

  const seen = new Set();
  let groupNo = 0;
  return (
    <div className="table-scroll">
      <table style={tableStyle}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: BORDER, background: '#e2e8f0' }}>
            {['No.', nameHeader, 'Size', '%', 'Box', 'Total Box'].map((h) => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const g = groupOf(r);
            const first = !seen.has(g);
            if (first) { seen.add(g); groupNo += 1; }
            const span = counts.get(g);
            return (
              <tr
                key={`${g}|${r.size}|${i}`}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                style={{ borderBottom: BORDER, cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {first && <td rowSpan={span} className="data-figure" style={{ ...tdStyle, verticalAlign: 'middle', borderRight: BORDER }}>{groupNo}</td>}
                {first && <td rowSpan={span} style={{ ...tdStyle, fontWeight: 600, verticalAlign: 'middle', borderRight: BORDER }}>{nameOf(r)}</td>}
                <td style={{ ...tdStyle, borderRight: BORDER }}>{r.size}</td>
                <td className="data-figure" style={{ ...tdStyle, borderRight: BORDER }}>{r.pct}%</td>
                <td className="data-figure" style={{ ...tdStyle, borderRight: BORDER }}>{r.qty}</td>
                {first && (
                  <td rowSpan={span} className="data-figure" style={{ ...tdStyle, fontWeight: 600, color: 'var(--color-accent)', verticalAlign: 'middle' }}>
                    {totalOf(r)}
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={6} style={{ ...tdStyle, padding: 'var(--space-3)', color: 'var(--color-text-muted)' }}>{emptyText}</td></tr>
          )}
          {rows.length > 0 && (
            <tr style={{ background: '#e2e8f0', borderTop: '2px solid var(--color-border)' }}>
              <td colSpan={5} style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b', textAlign: 'right', borderRight: BORDER }}>
                Grand Total Box
              </td>
              <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: '#1e293b' }}>
                {grandTotal}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const crumbStyle = (active) => ({
  fontSize: 'var(--font-size-sm)',
  fontWeight: active ? 700 : 400,
  color: active ? 'var(--color-text-primary)' : 'var(--color-accent)',
  background: 'none',
  border: 'none',
  cursor: active ? 'default' : 'pointer',
  padding: 0,
});

export default function SalesMap({ year, month }) {
  // ---- world level
  const [worldGeo, setWorldGeo] = useState(null);
  const [countryNames, setCountryNames] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- drill-down selection
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null); // { city, state }

  // ---- data for whatever is selected
  const [indiaGeo, setIndiaGeo] = useState(null);
  const [stateStats, setStateStats] = useState([]);
  const [cityStats, setCityStats] = useState([]);
  const [customerStats, setCustomerStats] = useState([]);
  // City coordinates for the selected country: india-cities.json for India,
  // world-cities/<iso2>.json elsewhere — [name, state, lat, lng].
  const [baseCities, setBaseCities] = useState([]);
  // India only: detailed towns/localities/industrial areas for the selected
  // state (public/data/cities/<state>.json, from India Post data).
  const [stateCityDataset, setStateCityDataset] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [hovered, setHovered] = useState(null);
  const latestStateFileRef = useRef(null);
  // GLOBE_VIEW: 3D globe by default, flat map if WebGL is unavailable or chosen
  const [view, setView] = useState(() => (webglSupported() ? 'globe' : 'flat'));

  useEffect(() => {
    setLoading(true);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    Promise.all([
      fetch('/data/world-countries.geojson').then((r) => r.json()),
      fetch('/data/country-names.json').then((r) => (r.ok ? r.json() : [])).catch(() => []),
      dispatchApi.getSalesMapCountries(year, month),
    ])
      .then(([geo, names, countries]) => {
        setWorldGeo(geo);
        setCountryNames(names);
        setCountryStats(countries);
        setError(null);
      })
      .catch((err) => setError(err.message || 'Could not load map data.'))
      .finally(() => setLoading(false));
  }, [year, month]);

  // ---- country name -> ISO code ("UAE", "U.A.E" and "United Arab Emirates"
  // all reach the same outline)
  const countryLookup = useMemo(() => {
    const m = new Map();
    countryNames.forEach(([iso2, name]) => m.set(normalizeCountryName(name), iso2));
    countryNames.forEach(([iso2, , alts]) => alts.forEach((a) => {
      const k = normalizeCountryName(a);
      if (!m.has(k)) m.set(k, iso2);
    }));
    if (!m.has('india')) m.set('india', 'IN');
    return m;
  }, [countryNames]);
  const resolveIso2 = useCallback((name) => countryLookup.get(normalizeCountryName(name)) || null, [countryLookup]);

  const distinctCountries = useMemo(
    () => distinctBy(countryStats, (r) => resolveIso2(r.country) || r.country, (r) => ({ country: r.country, iso2: resolveIso2(r.country) })),
    [countryStats, resolveIso2],
  );
  const countryByIso = useMemo(() => {
    const m = new Map();
    distinctCountries.forEach((c) => { if (c.iso2) m.set(c.iso2, c); });
    return m;
  }, [distinctCountries]);
  const distinctStates = useMemo(
    () => distinctBy(stateStats, (r) => normalizeStateName(r.state), (r) => ({ state: r.state })),
    [stateStats],
  );
  const stateByName = useMemo(() => {
    const m = new Map();
    distinctStates.forEach((s) => m.set(normalizeStateName(s.state), s));
    return m;
  }, [distinctStates]);
  const distinctCities = useMemo(
    () => distinctBy(cityStats, (r) => r.cityKey, (r) => ({ city: r.city, state: r.state, cityKey: r.cityKey })),
    [cityStats],
  );
  const distinctCustomers = useMemo(
    () => distinctBy(customerStats, (r) => r.customerId, (r) => ({ customerId: r.customerId, customer: r.customer })),
    [customerStats],
  );

  const selectedIso2 = useMemo(() => (selectedCountry ? resolveIso2(selectedCountry) : null), [selectedCountry, resolveIso2]);
  const isIndia = selectedIso2 === 'IN';
  const level = selectedCity ? 'customer' : selectedState ? 'city' : selectedCountry ? 'state' : 'world';

  const maxCountryPct = Math.max(0, ...distinctCountries.map((c) => c.pct));
  const maxStatePct = Math.max(0, ...distinctStates.map((s) => s.pct));

  // ---- outlines and projections
  const worldProjection = useMemo(() => (worldGeo ? fitProjection(worldGeo, geoNaturalEarth1()) : null), [worldGeo]);
  const worldPath = useMemo(() => (worldProjection ? geoPath(worldProjection) : null), [worldProjection]);

  // Name + % for every country that received material. Small countries sit
  // close together on a world map (India / UAE / Pakistan), so their labels
  // would print over each other — each one is nudged down until it clears
  // the labels already placed, with a thin line back to its country.
  const worldLabels = useMemo(() => {
    if (!worldPath || !worldGeo) return [];
    const raw = [];
    worldGeo.features.forEach((f) => {
      const stat = f.properties.iso2 ? countryByIso.get(f.properties.iso2) : null;
      if (!stat) return;
      const [x, y] = worldPath.centroid(f);
      if (Number.isNaN(x)) return;
      raw.push({ key: f.properties.iso2, text: `${stat.country} ${stat.pct}%`, x, y, pct: stat.pct });
    });
    raw.sort((a, b) => b.pct - a.pct);
    const placed = [];
    raw.forEach((l) => {
      let ly = l.y;
      for (let step = 0; step < 16; step += 1) {
        const candidate = l.y + step * 13;
        if (!placed.some((p) => Math.abs(p.x - l.x) < 90 && Math.abs(p.ly - candidate) < 13)) { ly = candidate; break; }
      }
      placed.push({ ...l, ly });
    });
    return placed;
  }, [worldPath, worldGeo, countryByIso]);

  const indiaProjection = useMemo(() => (indiaGeo ? fitProjection(indiaGeo) : null), [indiaGeo]);
  const indiaPath = useMemo(() => (indiaProjection ? geoPath(indiaProjection) : null), [indiaProjection]);

  const selectedStateFeature = useMemo(() => {
    if (!indiaGeo || !selectedState || !isIndia) return null;
    return indiaGeo.features.find((f) => normalizeStateName(f.properties.name) === normalizeStateName(selectedState)) || null;
  }, [indiaGeo, selectedState, isIndia]);
  const stateProjection = useMemo(() => (selectedStateFeature ? fitProjection(selectedStateFeature) : null), [selectedStateFeature]);
  const statePath = useMemo(() => (stateProjection ? geoPath(stateProjection) : null), [stateProjection]);

  // Outside India there are no state outlines, so a country is drawn as one
  // outline with every city plotted on it. Rotating to the country's own
  // meridian keeps countries that straddle the date line (Russia, Fiji) whole.
  const countryFeature = useMemo(() => {
    if (!worldGeo || !selectedIso2) return null;
    return worldGeo.features.find((f) => f.properties.iso2 === selectedIso2) || null;
  }, [worldGeo, selectedIso2]);
  const countryProjection = useMemo(() => {
    if (!countryFeature || isIndia) return null;
    return fitProjection(countryFeature, geoMercator().rotate([-geoCentroid(countryFeature)[0], 0]));
  }, [countryFeature, isIndia]);
  const countryPath = useMemo(() => (countryProjection ? geoPath(countryProjection) : null), [countryProjection]);

  // Where the city dots are drawn: on the selected state (India) or on the
  // country outline (everywhere else).
  const cityProjection = isIndia ? stateProjection : countryProjection;
  const cityPath = isIndia ? statePath : countryPath;
  const outlineFeature = isIndia ? selectedStateFeature : countryFeature;

  // ---- city coordinates ------------------------------------------------
  const cityIndex = useMemo(() => {
    const byState = new Map();
    const byLoose = new Map();
    const listByState = new Map();
    baseCities.forEach(([name, state, lat, lng]) => {
      const loose = cityLooseKey(name);
      if (!loose) return;
      const sKey = normalizeStateName(state);
      // Some entries only carry rough, whole-number-ish coordinates (Jaipur
      // as 27, 76 is ~100 km off). `precise` marks the trustworthy ones so a
      // more exact source can win over a rough one.
      const precise = Math.abs(lat * 100 - Math.round(lat * 100)) > 1e-6
        || Math.abs(lng * 100 - Math.round(lng * 100)) > 1e-6;
      if (!byState.has(`${sKey}|${loose}`)) {
        byState.set(`${sKey}|${loose}`, [lat, lng, precise]);
        if (!listByState.has(sKey)) listByState.set(sKey, []);
        listByState.get(sKey).push([loose, lat, lng]);
      }
      byLoose.set(loose, byLoose.has(loose) ? null : [lat, lng, precise]); // null = same name in several states
    });
    return { byState, byLoose, listByState };
  }, [baseCities]);

  const localIndex = useMemo(() => {
    const map = new Map();
    const list = [];
    stateCityDataset.forEach(([name, lat, lng]) => {
      const k = cityLooseKey(name);
      if (!k || map.has(k)) return;
      map.set(k, [lat, lng]);
      list.push([k, lat, lng]);
    });
    return { map, list };
  }, [stateCityDataset]);

  // Every place this city could be, most trustworthy first:
  //  1. (India) the small hand-verified list
  //  2. the country's city list with precise coordinates (same state)
  //  3. (India) the state's India Post places — towns, localities, industrial areas
  //  4. the city list's rough coordinates
  //  5. the meaningful word inside a longer name ("SITAPURA INDUSTRIAL AREA")
  //  6. the same name elsewhere in the country, if it is unique
  //  7. a close spelling within the same state (Kishangrah -> Kishangarh)
  // The map takes the first candidate that actually lands on the outline.
  const resolveCityCandidates = useCallback((city, state) => {
    const out = [];
    const loose = cityLooseKey(city);
    if (!loose) return out;
    const sKey = normalizeStateName(state);
    if (isIndia) {
      const manual = getCityCoordinates(city);
      if (manual) out.push(manual);
    }
    const lookupExact = (key) => {
      const base = cityIndex.byState.get(`${sKey}|${key}`);
      if (base && base[2]) return [base[0], base[1]];
      const local = localIndex.map.get(key);
      if (local) return local;
      if (base) return [base[0], base[1]];
      return null;
    };
    const exact = lookupExact(loose);
    if (exact) out.push(exact);
    cityNameTokens(city).forEach((t) => {
      const hit = lookupExact(cityLooseKey(t));
      if (hit) out.push(hit);
    });
    const anywhere = cityIndex.byLoose.get(loose);
    if (anywhere) out.push([anywhere[0], anywhere[1]]);
    if (loose.length >= 5) {
      const maxDist = loose.length <= 6 ? 1 : 2;
      let best = null;
      let bestDist = maxDist + 1;
      let ambiguous = false;
      localIndex.list.concat(cityIndex.listByState.get(sKey) || []).forEach(([key, lat, lng]) => {
        const d = editDistance(loose, key, maxDist);
        if (d > maxDist) return;
        if (d < bestDist) { best = [lat, lng]; bestDist = d; ambiguous = false; }
        else if (d === bestDist && best && (Math.abs(best[0] - lat) > 0.05 || Math.abs(best[1] - lng) > 0.05)) ambiguous = true;
      });
      if (best && !ambiguous) out.push(best);
    }
    return out;
  }, [isIndia, cityIndex, localIndex]);

  // Outside India the whole country's cities are loaded at once; once a
  // state is picked only that state's cities stay on the map.
  const visibleCities = useMemo(
    () => (isIndia || !selectedState ? distinctCities : distinctCities.filter((c) => c.state === selectedState)),
    [isIndia, selectedState, distinctCities],
  );

  // Every city gets a dot. A candidate location that lands nowhere near the
  // outline (a same-named place in another state or country) is rejected,
  // and a city with no usable location is placed in a small spiral around
  // the centre and flagged `approximate` — drawn as a dashed hollow dot,
  // never passed off as exact.
  const cityMarkers = useMemo(() => {
    if (!cityProjection || !cityPath || !outlineFeature) return [];
    const [[bx0, by0], [bx1, by1]] = cityPath.bounds(outlineFeature);
    const margin = 15;
    const [cx, cy] = cityPath.centroid(outlineFeature);
    let approxIndex = 0;
    return visibleCities.map((c) => {
      let projected = null;
      const candidates = resolveCityCandidates(c.city, c.state);
      for (let i = 0; i < candidates.length && !projected; i += 1) {
        const p = cityProjection([candidates[i][1], candidates[i][0]]);
        if (p && p[0] >= bx0 - margin && p[0] <= bx1 + margin && p[1] >= by0 - margin && p[1] <= by1 + margin) projected = p;
      }
      if (projected) return { ...c, x: projected[0], y: projected[1], approximate: false };
      const i = approxIndex;
      approxIndex += 1;
      const angle = i * 2.399963;
      const radius = 18 + Math.sqrt(i) * 22;
      return { ...c, x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius, approximate: true };
    });
  }, [cityProjection, cityPath, outlineFeature, visibleCities, resolveCityCandidates]);

  const unmappedCities = cityMarkers.filter((m) => m.approximate);
  const maxCityPct = Math.max(0, ...cityMarkers.map((m) => m.pct));

  const selectedCityMarker = useMemo(
    () => (selectedCity ? cityMarkers.find((m) => m.city === selectedCity.city && m.state === selectedCity.state) || null : null),
    [cityMarkers, selectedCity],
  );

  // Customer points: no per-customer coordinates exist, so these are
  // arranged in a deterministic spiral AROUND the city's real point — an
  // honest visual approximation of "who's around here and how much they
  // bought", not a claim about anyone's street address.
  const maxCustomerQty = Math.max(0, ...distinctCustomers.map((c) => c.qty));
  const customerPoints = useMemo(() => {
    if (!selectedCityMarker) return [];
    return distinctCustomers.map((c, i) => {
      const angle = i * 2.399963; // golden-angle spiral for even spacing
      const radius = 14 + Math.sqrt(i) * 16;
      return {
        ...c,
        x: selectedCityMarker.x + Math.cos(angle) * radius,
        y: selectedCityMarker.y + Math.sin(angle) * radius,
        r: maxCustomerQty > 0 ? 4 + (c.qty / maxCustomerQty) * 14 : 4,
      };
    });
  }, [selectedCityMarker, distinctCustomers, maxCustomerQty]);

  const svgViewBox = useMemo(() => {
    if (level === 'customer' && selectedCityMarker) {
      const half = 130;
      return `${selectedCityMarker.x - half} ${selectedCityMarker.y - half} ${half * 2} ${half * 2}`;
    }
    // Outside India, picking a state zooms to where that state's cities are.
    if (!isIndia && level === 'city' && cityMarkers.length > 0) {
      const xs = cityMarkers.map((m) => m.x);
      const ys = cityMarkers.map((m) => m.y);
      const w = Math.max(...xs) - Math.min(...xs);
      const h = Math.max(...ys) - Math.min(...ys);
      const vw = Math.max(w * 1.5, (h * 1.5 * WIDTH) / HEIGHT, 220);
      const vh = (vw * HEIGHT) / WIDTH;
      const cx = (Math.max(...xs) + Math.min(...xs)) / 2;
      const cy = (Math.max(...ys) + Math.min(...ys)) / 2;
      return `${cx - vw / 2} ${cy - vh / 2} ${vw} ${vh}`;
    }
    return `0 0 ${WIDTH} ${HEIGHT}`;
  }, [level, selectedCityMarker, isIndia, cityMarkers]);

  // ---- globe data: the flat map's own positions, converted back to lat / lng ----
  const globeData = useMemo(() => {
    const none = {
      showCities: false, cityPoints: [], customerPoints: [], focus: { lat: 22, lng: 78, alt: 2.4 },
    };
    if (view !== 'globe') return none;
    if (level === 'world' || !selectedIso2) return none;
    const inv = (x, y) => {
      if (!cityProjection || !cityProjection.invert) return null;
      const p = cityProjection.invert([x, y]);
      return p && Number.isFinite(p[0]) && Number.isFinite(p[1]) ? { lng: p[0], lat: p[1] } : null;
    };
    const showCities = level === 'city' || (level === 'state' && !isIndia && !countryLoading);
    const toPoint = (m) => {
      const ll = inv(m.x, m.y);
      return ll ? { ...m, ...ll } : null;
    };
    let cityPoints = [];
    if (showCities) cityPoints = cityMarkers.map(toPoint).filter(Boolean);
    else if (level === 'customer' && selectedCityMarker) cityPoints = [toPoint(selectedCityMarker)].filter(Boolean);
    const customerPts = level === 'customer' ? customerPoints.map(toPoint).filter(Boolean) : [];

    const spanOf = (pts) => {
      const lats = pts.map((p) => p.lat);
      const lngs = pts.map((p) => p.lng);
      return {
        lat: (Math.max(...lats) + Math.min(...lats)) / 2,
        lng: (Math.max(...lngs) + Math.min(...lngs)) / 2,
        span: Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs)),
      };
    };
    const featSpan = (f) => {
      const [[w, s], [e, n]] = geoBounds(f);
      const lngSpan = e >= w ? e - w : e - w + 360;
      const [clng, clat] = geoCentroid(f);
      return { lat: clat, lng: clng, span: Math.min(120, Math.max(lngSpan, n - s)) };
    };
    const altOf = (span) => Math.min(2.4, Math.max(0.05, span / 42 + 0.1));
    let focus = none.focus;
    try {
      if (level === 'customer' && cityPoints.length) {
        const b = spanOf(cityPoints.concat(customerPts));
        focus = { lat: b.lat, lng: b.lng, alt: altOf(Math.max(0.12, b.span * 2.4)) };
      } else if (level === 'city' && isIndia && selectedStateFeature) {
        const b = featSpan(selectedStateFeature);
        focus = { lat: b.lat, lng: b.lng, alt: altOf(b.span * 1.1) };
      } else if (level === 'city' && cityPoints.length) {
        const b = spanOf(cityPoints);
        focus = { lat: b.lat, lng: b.lng, alt: altOf(Math.max(2, b.span * 1.8)) };
      } else if (countryFeature) {
        const b = featSpan(countryFeature);
        focus = { lat: b.lat, lng: b.lng, alt: altOf(b.span * 1.15) };
      }
    } catch (err) { /* keep default view */ }
    return {
      showCities, cityPoints, customerPoints: customerPts, focus,
    };
  }, [view, level, selectedIso2, isIndia, countryLoading, cityProjection, cityMarkers, selectedCityMarker, customerPoints, selectedStateFeature, countryFeature]);

  // ---- navigation ------------------------------------------------------
  const loadJson = (url, setter) => fetch(url).then((r) => (r.ok ? r.json() : [])).then(setter).catch(() => setter([]));

  const openCountry = (countryName) => {
    setSelectedCountry(countryName);
    setSelectedState(null);
    setSelectedCity(null);
    setStateStats([]);
    setCityStats([]);
    setBaseCities([]);
    setStateCityDataset([]);
    setCountryLoading(true);
    const iso2 = resolveIso2(countryName);
    const tasks = [dispatchApi.getSalesMapStates(year, month, countryName).then(setStateStats).catch(() => setStateStats([]))];
    if (iso2 === 'IN') {
      if (!indiaGeo) tasks.push(fetch('/data/india-states.geojson').then((r) => r.json()).then(setIndiaGeo).catch(() => {}));
      tasks.push(loadJson('/data/india-cities.json', setBaseCities));
    } else {
      tasks.push(dispatchApi.getSalesMapCities(undefined, year, month, countryName).then(setCityStats).catch(() => setCityStats([])));
      if (iso2) tasks.push(loadJson(`/data/world-cities/${iso2.toLowerCase()}.json`, setBaseCities));
    }
    Promise.all(tasks).finally(() => setCountryLoading(false));
  };

  const openState = (stateName) => {
    setSelectedState(stateName);
    setSelectedCity(null);
    if (!isIndia) return; // cities of a foreign country are already loaded
    setCityStats([]);
    setCityLoading(true);
    const slug = stateFileSlug(stateName);
    latestStateFileRef.current = slug;
    setStateCityDataset([]);
    fetch(`/data/cities/${slug}.json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { if (latestStateFileRef.current === slug) setStateCityDataset(rows); })
      .catch(() => {});
    dispatchApi.getSalesMapCities(stateName, year, month, selectedCountry)
      .then(setCityStats)
      .catch(() => setCityStats([]))
      .finally(() => setCityLoading(false));
  };

  const openCity = (cityName, stateName) => {
    if (!isIndia) setSelectedState(stateName);
    setSelectedCity({ city: cityName, state: stateName });
    setCustomerStats([]);
    setCustomerLoading(true);
    dispatchApi.getSalesMapCustomers(cityName, year, month, selectedCountry, stateName)
      .then(setCustomerStats)
      .catch(() => setCustomerStats([]))
      .finally(() => setCustomerLoading(false));
  };

  const backToWorld = () => { setSelectedCountry(null); setSelectedState(null); setSelectedCity(null); };
  const backToCountry = () => { setSelectedState(null); setSelectedCity(null); };
  const backToState = () => setSelectedCity(null);

  if (loading) return <p style={{ color: 'var(--color-text-secondary)' }}>Loading map…</p>;
  if (error) return <p style={{ color: 'var(--color-danger)' }}>Could not load the sales map: {error}</p>;

  const showCityDots = level === 'city' || (level === 'state' && !isIndia && !countryLoading);
  const unmatchedCountries = distinctCountries.filter((c) => !c.iso2);
  const countryLabel = selectedCountry ? (cleanCityName(selectedCountry) || selectedCountry) : '';
  const cityDisplay = selectedCity ? (cleanCityName(selectedCity.city) || selectedCity.city) : '';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={backToWorld} style={crumbStyle(level === 'world')}>World</button>
        {selectedCountry && (
          <>
            <span style={{ color: 'var(--color-text-muted)' }}>/</span>
            <button type="button" onClick={backToCountry} style={crumbStyle(level === 'state')}>{countryLabel}</button>
          </>
        )}
        {selectedState && (
          <>
            <span style={{ color: 'var(--color-text-muted)' }}>/</span>
            <button type="button" onClick={backToState} style={crumbStyle(level === 'city')}>{selectedState}</button>
          </>
        )}
        {selectedCity && (
          <>
            <span style={{ color: 'var(--color-text-muted)' }}>/</span>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700 }}>{cityDisplay}</span>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 560px', minWidth: 320 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {[['globe', '3D Globe'], ['flat', 'Flat map']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setView(k)}
                style={{
                  padding: '4px 12px',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  borderRadius: 999,
                  cursor: 'pointer',
                  border: BORDER,
                  background: view === k ? 'var(--color-accent)' : 'transparent',
                  color: view === k ? '#fff' : 'var(--color-text-secondary)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {view === 'globe' ? (
            <SalesGlobe
              level={level}
              isIndia={isIndia}
              worldFeatures={worldGeo ? worldGeo.features : []}
              countryByIso={countryByIso}
              maxCountryPct={maxCountryPct}
              selectedIso2={selectedIso2}
              indiaFeatures={indiaGeo ? indiaGeo.features : []}
              stateByName={stateByName}
              maxStatePct={maxStatePct}
              normalizeStateName={normalizeStateName}
              selectedState={selectedState}
              cityPoints={globeData.cityPoints}
              showCities={globeData.showCities}
              maxCityPct={maxCityPct}
              customerPoints={globeData.customerPoints}
              maxCustomerQty={maxCustomerQty}
              focus={globeData.focus}
              colorFor={colorForPct}
              onCountry={(stat) => openCountry(stat.country)}
              onState={(stat) => openState(stat.state)}
              onCity={(c) => openCity(c.city, c.state)}
              onUnsupported={() => setView('flat')}
            />
          ) : (
          <svg
            viewBox={svgViewBox}
            style={{ width: '100%', height: 'auto', background: '#f8fafc', borderRadius: 'var(--radius-sm)', border: BORDER }}
          >
            {/* WORLD: every country, coloured by its share, named where material went */}
            {level === 'world' && worldPath && worldGeo.features.map((f) => {
              const stat = f.properties.iso2 ? countryByIso.get(f.properties.iso2) : null;
              const pct = stat ? stat.pct : 0;
              return (
                <g key={`${f.properties.iso2}-${f.properties.name}`}>
                  <path
                    d={worldPath(f)}
                    fill={colorForPct(pct, maxCountryPct)}
                    stroke="#94a3b8"
                    strokeWidth={0.4}
                    style={{ cursor: stat ? 'pointer' : 'default' }}
                    onClick={() => stat && openCountry(stat.country)}
                    onMouseEnter={() => stat && setHovered({ name: stat.country, pct })}
                    onMouseLeave={() => setHovered(null)}
                  />
                </g>
              );
            })}

            {level === 'world' && worldLabels.map((l) => (
              <g key={l.key} style={{ pointerEvents: 'none' }}>
                {l.ly !== l.y && <line x1={l.x} y1={l.y} x2={l.x} y2={l.ly - 9} stroke="#475569" strokeWidth={0.6} />}
                <text
                  x={l.x}
                  y={l.ly}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={700}
                  fill="#1e293b"
                  stroke="#ffffff"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {l.text}
                </text>
              </g>
            ))}

            {/* INDIA: state outlines coloured by share */}
            {level === 'state' && isIndia && indiaGeo && indiaPath && indiaGeo.features.map((f) => {
              const name = f.properties.name;
              const stat = stateByName.get(normalizeStateName(name));
              const pct = stat ? stat.pct : 0;
              const centroid = indiaPath.centroid(f);
              return (
                <g key={name}>
                  <path
                    d={indiaPath(f)}
                    fill={colorForPct(pct, maxStatePct)}
                    stroke="#94a3b8"
                    strokeWidth={0.5}
                    style={{ cursor: stat ? 'pointer' : 'default' }}
                    onClick={() => stat && openState(stat.state)}
                    onMouseEnter={() => setHovered({ name, pct })}
                    onMouseLeave={() => setHovered(null)}
                  />
                  {pct > 0 && !Number.isNaN(centroid[0]) && (
                    <text x={centroid[0]} y={centroid[1]} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1e293b" style={{ pointerEvents: 'none' }}>
                      {pct}%
                    </text>
                  )}
                </g>
              );
            })}

            {/* the outline the city dots sit on */}
            {level !== 'world' && !(level === 'state' && isIndia) && outlineFeature && cityPath && (
              <path d={cityPath(outlineFeature)} fill="#f1f5f9" stroke="#94a3b8" strokeWidth={1} />
            )}

            {showCityDots && cityMarkers.map((m) => (
              <g
                key={m.cityKey}
                onClick={() => openCity(m.city, m.state)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHovered({ name: cleanCityName(m.city) || m.city, pct: m.pct })}
                onMouseLeave={() => setHovered(null)}
              >
                <circle
                  cx={m.x}
                  cy={m.y}
                  r={6 + (maxCityPct > 0 ? (m.pct / maxCityPct) * 14 : 0)}
                  fill={m.approximate ? '#93c5fd' : '#2563eb'}
                  fillOpacity={m.approximate ? 0.5 : 0.75}
                  stroke="#1e40af"
                  strokeWidth={1}
                  strokeDasharray={m.approximate ? '3 2' : undefined}
                />
                {cityMarkers.length <= MAX_LABELED_POINTS && (
                  <text x={m.x} y={m.y - 10 - (maxCityPct > 0 ? (m.pct / maxCityPct) * 14 : 0)} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1e293b">
                    {cleanCityName(m.city) || m.city} ({m.pct}%)
                  </text>
                )}
              </g>
            ))}

            {level === 'customer' && selectedCityMarker && (
              <circle cx={selectedCityMarker.x} cy={selectedCityMarker.y} r={4} fill="#1e40af" />
            )}
            {level === 'customer' && customerPoints.map((c) => (
              <g key={c.customerId} onMouseEnter={() => setHovered({ name: c.customer, qty: c.qty })} onMouseLeave={() => setHovered(null)}>
                <circle cx={c.x} cy={c.y} r={c.r} fill="#16a34a" fillOpacity={0.8} stroke="#15803d" strokeWidth={1} />
                {customerPoints.length <= MAX_LABELED_POINTS && (
                  <text x={c.x} y={c.y - c.r - 4} textAnchor="middle" fontSize={10} fontWeight={600} fill="#1e293b">
                    {c.customer} ({c.qty} Box)
                  </text>
                )}
              </g>
            ))}
          </svg>
          )}

          {view !== 'globe' && (level === 'world' || (level === 'state' && isIndia)) && (
            <Legend maxPct={level === 'world' ? maxCountryPct : maxStatePct} label="% of total sales" />
          )}
          {showCityDots && cityMarkers.length > MAX_LABELED_POINTS && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
              {cityMarkers.length} cities here — hover a dot, or see the table on the right, for names.
            </p>
          )}
          {level === 'customer' && customerPoints.length > MAX_LABELED_POINTS && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
              {customerPoints.length} customers here — hover a dot, or see the table on the right, for names.
            </p>
          )}
          {level !== 'world' && !isIndia && !countryFeature && !countryLoading && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
              This country&apos;s name could not be matched to the world map, so it can&apos;t be drawn — its sales are still in the table.
            </p>
          )}
          {hovered && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 4 }}>
              {hovered.pct !== undefined ? `${hovered.name}: ${hovered.pct}% of total sales` : `${hovered.name}: ${hovered.qty} Box total`}
            </p>
          )}
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 260 }}>
          {level === 'world' && (
            <>
              <h4 style={{ margin: '0 0 8px' }}>Countries by sales share</h4>
              <SizeWiseTable
                rows={countryStats}
                groupOf={(r) => r.country}
                nameOf={(r) => r.country}
                nameHeader="Country"
                totalOf={(r) => r.countryGrandTotalQty}
                onRowClick={(r) => openCountry(r.country)}
                emptyText="No dispatches in this period."
              />
              {unmatchedCountries.length > 0 && (
                <p style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 6 }}>
                  {unmatchedCountries.length === 1 ? 'This country' : 'These countries'} could not be matched to the world map by name
                  ({unmatchedCountries.map((c) => c.country).join(', ')}) — still counted in the table.
                </p>
              )}
            </>
          )}

          {level === 'state' && (
            <>
              <h4 style={{ margin: '0 0 8px' }}>{isIndia ? 'States' : 'States / regions'} in {countryLabel}</h4>
              {countryLoading ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p> : (
                <SizeWiseTable
                  rows={stateStats}
                  groupOf={(r) => r.state}
                  nameOf={(r) => r.state}
                  nameHeader="State"
                  totalOf={(r) => r.stateGrandTotalQty}
                  onRowClick={(r) => openState(r.state)}
                  emptyText="No dispatches recorded for this country."
                />
              )}
              {!isIndia && !countryLoading && (
                <p style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 6 }}>
                  State outlines are drawn for India only. Here every city is plotted on the country map; pick a state to zoom in on its cities.
                </p>
              )}
            </>
          )}

          {level === 'city' && (
            <>
              <h4 style={{ margin: '0 0 8px' }}>Cities in {selectedState}</h4>
              {cityLoading ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p> : (
                <SizeWiseTable
                  rows={isIndia ? cityStats : cityStats.filter((r) => r.state === selectedState)}
                  groupOf={(r) => r.cityKey}
                  nameOf={(r) => cleanCityName(r.city) || r.city}
                  nameHeader="City"
                  totalOf={(r) => r.cityGrandTotalQty}
                  onRowClick={(r) => openCity(r.city, r.state)}
                  emptyText="No cities recorded."
                />
              )}
            </>
          )}

          {level === 'state' && !isIndia && !countryLoading && cityStats.length > 0 && (
            <>
              <h4 style={{ margin: '16px 0 8px' }}>Cities in {countryLabel}</h4>
              <SizeWiseTable
                rows={cityStats}
                groupOf={(r) => r.cityKey}
                nameOf={(r) => cleanCityName(r.city) || r.city}
                nameHeader="City"
                totalOf={(r) => r.cityGrandTotalQty}
                onRowClick={(r) => openCity(r.city, r.state)}
                emptyText="No cities recorded."
              />
            </>
          )}

          {(level === 'city' || (level === 'state' && !isIndia)) && unmappedCities.length > 0 && (
            <p style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 6 }}>
              {unmappedCities.length} {unmappedCities.length === 1 ? 'city is' : 'cities are'} shown as a dashed dot at an approximate position (exact location not found): {unmappedCities.slice(0, 15).map((m) => cleanCityName(m.city) || m.city).join(', ')}{unmappedCities.length > 15 ? '…' : ''}. Sales figures are still exact.
            </p>
          )}

          {level === 'customer' && (
            <>
              <h4 style={{ margin: '0 0 8px' }}>Customers in {cityDisplay}</h4>
              {customerLoading ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p> : (
                <SizeWiseTable
                  rows={customerStats}
                  groupOf={(r) => r.customerId}
                  nameOf={(r) => r.customer}
                  nameHeader="Customer"
                  totalOf={(r) => r.customerGrandTotalQty}
                  emptyText="No dispatches recorded for this city."
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
